// Strict CSV parser that matches Ruby's CSV library (RFC 4180, liberal_parsing: false).
// The server raises CSV::MalformedCSVError for these same syntax problems.

export class CsvSyntaxError extends Error {
  constructor(kind, line) {
    const userMessage = csvSyntaxUserMessage(kind, line)
    super(userMessage)
    this.name = "CsvSyntaxError"
    this.kind = kind
    this.line = line
    this.userMessage = userMessage
  }
}

function csvSyntaxUserMessage(kind, line) {
  if (kind === "after_quoted") {
    return `This CSV file has a formatting problem on line ${line}: there is extra text after a quoted field. This often happens when exporting from Excel. Open the file, look for a stray quotation mark on that line, and save it again as CSV.`
  }

  if (kind === "unclosed") {
    return `This CSV file has a formatting problem on line ${line}: a quoted field is missing its closing quotation mark. Open the file, check that line, and make sure every opening quote has a matching closing quote.`
  }

  if (kind === "illegal_quoting") {
    return `This CSV file has a formatting problem on line ${line}: there is a quotation mark in the middle of a field that isn't escaped. Quotes inside a value must be written as two quotes (""), or the whole field must be wrapped in quotes.`
  }

  return `This CSV file has a formatting problem on line ${line}. Please check that line for stray quotes or line breaks and save the file again as CSV.`
}

function newlineLengthAt(text, index) {
  const char = text[index]
  if (char === "\n") return 1
  if (char === "\r") return text[index + 1] === "\n" ? 2 : 1
  return 0
}

export function parseCsv(text) {
  let csvText = String(text || "")
  if (csvText.charCodeAt(0) === 0xFEFF) {
    csvText = csvText.slice(1)
  }

  const rows = []
  let currentRow = []
  let currentValue = ""
  // field_start: ready for a new field
  // in_unquoted: reading a field that did not start with a quote
  // in_quoted: reading a field that started with a quote
  // after_quoted: just closed a quoted field; next char must be comma, newline, or end of file
  let state = "field_start"
  let line = 1
  let quoteStartLine = 1

  const pushField = () => {
    currentRow.push(currentValue)
    currentValue = ""
  }

  const pushRow = () => {
    rows.push(currentRow)
    currentRow = []
  }

  for (let index = 0; index < csvText.length; index += 1) {
    const char = csvText[index]
    const newlineLength = newlineLengthAt(csvText, index)

    if (state === "in_quoted") {
      if (char === '"') {
        if (csvText[index + 1] === '"') {
          currentValue += '"'
          index += 1
        } else {
          state = "after_quoted"
        }
      } else {
        currentValue += char
        if (newlineLength > 0) {
          if (newlineLength === 2) {
            currentValue += csvText[index + 1]
            index += 1
          }
          line += 1
        }
      }
      continue
    }

    if (newlineLength > 0) {
      pushField()
      pushRow()
      state = "field_start"
      line += 1
      if (newlineLength === 2) index += 1
      continue
    }

    if (char === ",") {
      pushField()
      state = "field_start"
      continue
    }

    if (char === '"') {
      if (state === "field_start") {
        state = "in_quoted"
        quoteStartLine = line
      } else if (state === "after_quoted") {
        throw new CsvSyntaxError("after_quoted", line)
      } else {
        throw new CsvSyntaxError("illegal_quoting", line)
      }
      continue
    }

    if (state === "after_quoted") {
      throw new CsvSyntaxError("after_quoted", line)
    }

    state = "in_unquoted"
    currentValue += char
  }

  if (state === "in_quoted") {
    throw new CsvSyntaxError("unclosed", quoteStartLine)
  }

  if (state === "after_quoted" || state === "in_unquoted" || currentValue.length > 0 || currentRow.length > 0) {
    pushField()
    pushRow()
  }

  return rows
}

function csvHasContent(rows) {
  return rows.some((row) => row.some((cell) => String(cell || "").trim() !== ""))
}

export function validateCsvText(text) {
  try {
    const rows = parseCsv(text)
    if (!csvHasContent(rows)) {
      return { valid: false, rows, message: "This CSV file appears to be empty." }
    }
    return { valid: true, rows }
  } catch (error) {
    if (error instanceof CsvSyntaxError) {
      return { valid: false, line: error.line, message: error.userMessage }
    }
    return { valid: false, message: "Unable to read that CSV file. Please check the formatting and try again." }
  }
}

export function looksLikeCsvFile(file) {
  if (!file) return false

  const fileName = (file.name || "").toLowerCase()
  if (fileName.endsWith(".csv")) return true

  const fileType = (file.type || "").toLowerCase()
  return fileType === "text/csv" || fileType === "application/csv"
}

export async function validateCsvFile(file) {
  if (!file) return { valid: true }

  const text = await file.text()
  return validateCsvText(text)
}
